import '@/polyfills-loader';
import type { Product } from '@/domain/Product';

type Specs = {
  decaf: { value: boolean };
  cupSizes: { measure?: number; value: string }[];
  intensity: { value: number };
  aromatics: { value: string }[];
};

type SimplifiedFields = {
  decaf: boolean;
  cupSizes: string[];
  intensity: number;
  aromatics: string[];
  isMilky: boolean;
  isIced: boolean;
};

/**
 * FACTORS determine how much each attribute impacts the overall similarity score
 * in the alternative algorithm. Higher values represent a greater penalty for mismatches.
 * The calculation prioritizes attributes with no mismatch (distance = 0),
 * while penalizing those with increasing degrees of mismatch.
 *
 * For example, the intensity factor penalizes products based on how far their intensity
 * is from the target product's intensity:
 *
 * - Target coffee intensity = 8, compared coffee intensity = 8 → Distance = 0 (perfect match)
 * - Target coffee intensity = 8, compared coffee intensity = 7 → Distance = 2
 *   (1 unit difference * 2 penalty factor)
 * - Target coffee intensity = 8, compared coffee intensity = 6 → Distance = 4
 *   (2 units difference * 2 penalty factor)
 *
 * Similar logic applies to other attributes, such as `cupSizes` or `aromatics`, with their
 * respective factors defining the weight of mismatches.
 *
 * Factors for binary attributes (e.g., `milky`, `iced`) impose a high penalty when the attribute
 * does not match. For example, if the target product is iced but the compared product is not,
 * a penalty of 100 is added to the distance.
 */
const FACTORS = {
  decaf: 10,
  cupSizes: 8,
  intensity: 2,
  aromatics: 1,

  // These are only applied when the target product is milky or iced
  milky: 100, // High penalty
  iced: 100 // High penalty
};

/**
 * This function filters the products on some criteria and then calculates the weighted distance
 * between the target product and a list of products, sorting them by similarity (minimum distance).
 */
export function getAlternativeProducts(
  targetProduct: Product,
  allProducts: Product[]
) {
  const targetSpecs = simplifyFields(targetProduct);
  const technologies = new Set(targetProduct.technologies);
  const isFlavouredTarget = isFlavouredCoffee(targetProduct);
  const isDecafTarget = targetSpecs.decaf;

  return allProducts
    .filter((product) => {
      let baseFilter =
        product.inStock &&
        isFlavouredTarget === isFlavouredCoffee(product) &&
        new Set(product.technologies).isSupersetOf(technologies);

      // When a Coffee is decaffeinated, it should only show other decaffeinated Coffees
      if (isDecafTarget && 'value' in product.specifications.decaf) {
        baseFilter &&= product.specifications.decaf.value as boolean;
      }

      return baseFilter;
    })
    .map((product) => ({
      product,
      distance: calculateDistance(targetSpecs, simplifyFields(product))
    }))
    .sort((a, b) => a.distance - b.distance);
}

function simplifyFields(product: Product): SimplifiedFields {
  const categoriesString = [...product.categories.keys()]?.join().toLowerCase();
  const { intensity, decaf, cupSizes, aromatics } =
    product.specifications as unknown as Specs;

  return {
    intensity: intensity?.value ?? 0,
    decaf: decaf?.value ?? false,
    cupSizes: cupSizes?.map((c) => c.value),
    aromatics: aromatics?.map((a) => a.value),

    // It is not 100% accurate, but better than nothing
    isMilky: categoriesString.includes('milk'),
    isIced: categoriesString.includes('ice')
  };
}

function calculateDistance(
  targetFields: SimplifiedFields,
  productFields: SimplifiedFields
): number {
  const decafDiff = boolDifference(
    targetFields.decaf,
    productFields.decaf,
    FACTORS.decaf
  );

  const cupSizesDiff = listDifference(
    targetFields.cupSizes,
    productFields.cupSizes,
    FACTORS.cupSizes
  );

  const intensityDiff = numericDifference(
    targetFields.intensity,
    productFields.intensity,
    FACTORS.intensity
  );

  const aromaticsDiff = listDifference(
    targetFields.aromatics,
    productFields.aromatics,
    FACTORS.aromatics
  );

  // Only when a Coffee is milky, it should priorize other milky Coffees
  const milkyDiff =
    targetFields.isMilky && !productFields.isMilky ? FACTORS.milky : 0;

  // Only when a Coffee is iced, it should priorize other iced Coffees
  const icedDiff =
    targetFields.isIced && !productFields.isIced ? FACTORS.iced : 0;

  return (
    icedDiff +
    milkyDiff +
    decafDiff +
    cupSizesDiff +
    intensityDiff +
    aromaticsDiff
  );
}

function boolDifference(
  targetBool: boolean,
  productBool: boolean,
  weight: number
): number {
  return targetBool !== productBool ? weight : 0;
}

function numericDifference(
  targetNum: number,
  productNum: number,
  weight: number
): number {
  return Math.abs(targetNum - productNum) * weight;
}

function listDifference(
  targetList: string[],
  productList: string[],
  weight: number
): number {
  return new Set(targetList).difference(new Set(productList)).size * weight;
}

// Note: When a coffee is set without intensity, it is considered a flavoured coffee
// The only exception is bundled products that also do not have an intensity value.
function isFlavouredCoffee(product: Product) {
  const { intensity } = product.specifications as unknown as Specs;
  return !product.bundle && product.type === 'capsule' && !intensity?.value;
}
